import { defineStore } from 'pinia'
import { useVehiclesStore } from './vehicles'
// import { useVehicleStore } from './vehicleStore' // Removed old import
import { useFrontPageStore } from './frontPage'
import { useConfigStore } from './useConfigStore'
import { useSiteStore } from './site'
import type { Vehicle } from '~/types'

interface VehicleEnquiryPopUp {
  show: boolean;
  item: Vehicle | null;
}

export const useAgentStore = defineStore('agent', {
  state: () => ({
    searchState: null as any,
    showSavesList: false,
    loading: false,
    error: null as string | null,
    isDataLoaded: false,
    scrollPosition: 0,
    resultsSearchSwitch: false,
    globalSearchSwitch: false,
    vehicleEnquiryGallery: false,
    vehicleEnquiryPopUp: { show: false, item: null } as VehicleEnquiryPopUp,
    isSortByDirectionChanged: false,
    isSortByChanged: false
  }),

  actions: {
    // URL and History Management
    async initializeFromUrl() {
      if (!this.isDataLoaded) {
        await this.fetchInitialData()
      }
      
      // Only run URL parsing logic on the client
      if (process.client) {
        const vehiclesStore = useVehiclesStore()
        const params = new URLSearchParams(window.location.search)
        const stockId = params.get('stock')
        
        if (stockId) {
          const vehicle = vehiclesStore.vehicles.find(v => v.stockid?.toString() === stockId.toString())
          
          if (vehicle) {
            this.vehicleEnquiryPopUp = { show: true, item: vehicle }
          } else {
            console.warn('[AgentStore] No matching vehicle found for stock ID:', stockId)
          }
        }
      }
    },

    handleUrlUpdate(show: boolean, item: Vehicle | null) {
      // Only run URL manipulation logic on the client
      if (process.client) {
        const params = new URLSearchParams(window.location.search)
        
        if (show && item?.stockid) {
          params.set('stock', item.stockid)
        } else {
          params.delete('stock')
        }

        const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`
        
        try {
          if (show && item) {
            window.history.pushState({ path: newUrl }, '', newUrl)
          } else {
            window.history.replaceState({ path: newUrl }, '', newUrl)
          }
        } catch (error) {
          console.error('Error updating URL:', error)
        }
      }
    },

    // Data Management
    async fetchInitialData() {
      const vehiclesStore = useVehiclesStore()
      
      if (this.isDataLoaded) {
        return
      }

      this.loading = true
      this.error = null
      
      try {
        this.isDataLoaded = true
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to initialize data'
        this.isDataLoaded = false
      } finally {
        this.loading = false
      }
    },

    // UI State Management
    setScrollPosition(position: number) {
      this.scrollPosition = position
      localStorage.setItem('scrollPosition', position.toString())
    },

    resetScrollPosition() {
      this.scrollPosition = 0
      localStorage.setItem('scrollPosition', "0")
      localStorage.setItem('pageSize', "12")
    },

    // Search and Filter Management
    saveSearchState(state: any) {
      this.searchState = state
    },

    clearSearchState() {
      this.searchState = null
    },

    // Vehicle Enquiry Management
    setVehicleEnquiryPopUp({ show, item }: VehicleEnquiryPopUp) {
      this.handleUrlUpdate(show, item)
      this.vehicleEnquiryPopUp = { show, item }
    },

    // Favorites Management
    async initializeSavedVehicles() {
      // This logic is likely redundant now as vehiclesStore loads favorites on mount.
      // const vehiclesStore = useVehiclesStore();
      // No explicit action needed here for now, unless there's specific agent logic.
      // console.log('[AgentStore] initializeSavedVehicles called, favorites loaded by vehiclesStore.');
    },

    // Search UI Management
    toggleResultsSearch(value: boolean) {
      this.resultsSearchSwitch = value
    },

    toggleGlobalSearch(value: boolean) {
      this.globalSearchSwitch = value
    },

    toggleSavesList(value: boolean) {
      this.showSavesList = value
    },

    setVehicleEnquiryGallery(value: boolean) {
      this.vehicleEnquiryGallery = value
    },

    // Sort Management
    changeSortingDirection(direction: string) {
      this.isSortByDirectionChanged = true
      const frontPageStore = useFrontPageStore()
      if ('setSortDirection' in frontPageStore) {
        (frontPageStore as any).setSortDirection(direction)
      }
    },

    changeSortingBy(value: string) {
      this.isSortByChanged = true
      const frontPageStore = useFrontPageStore()
      if ('setSortBy' in frontPageStore) {
        (frontPageStore as any).setSortBy(value)
      }
    }
  }
}) 